<script lang="ts">
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Text, Select, Input } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { valueOutOfRange } from 'src/helpers/utilities';

	export let device: Device;

	const id = real_id(device.id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;
	const uiConfig = settings?.['presencec_ui:0'] ?? {};

	const LED_BRIGHTNESS_MIN = 0;
	const LED_BRIGHTNESS_MAX = 100;

	const LED_MODE_OPTIONS = [
		{
			title: $format('option_led_mode_motion_only', {
				default: 'LED indicates motion detection only',
			}),
			value: 'motion',
		},
		{
			title: $format('option_led_mode_network_only', {
				default: 'LED indicates network connectivity only',
			}),
			value: 'connectivity',
		},
		{
			title: $format('option_led_mode_motion_and_connectivity', {
				default: 'LED indicates both motion and connectivity',
			}),
			value: 'motion_connectivity',
		},
		{
			title: $format('option_led_mode_disabled', { default: 'LED disabled' }),
			value: 'off',
		},
	];

	let led_brightness: number = uiConfig?.led_brightness ?? 8;
	let led_mode: string = uiConfig?.led_mode ?? 'motion_connectivity';

	let cacheLedBrightness = led_brightness;
	let cacheLedMode = led_mode;
	let loading = false;

	const findOption = (val: string) =>
		LED_MODE_OPTIONS.find((o) => o.value === val) ?? LED_MODE_OPTIONS[2];

	$: ledModeValue = findOption(led_mode);

	$: brightnessError = valueOutOfRange(led_brightness, LED_BRIGHTNESS_MIN, LED_BRIGHTNESS_MAX)
		? $format('min_max_error_description', {
				values: { min: LED_BRIGHTNESS_MIN, max: LED_BRIGHTNESS_MAX },
				default: 'Value must be between {min} and {max}.',
			})
		: '';

	$: hasChanges = cacheLedBrightness !== led_brightness || cacheLedMode !== led_mode;

	const save = async () => {
		if (!hasChanges || brightnessError) return;

		loading = true;
		const brightnessNum = Number(led_brightness);
		const params = {
			id: device.channel,
			config: { led_brightness: brightnessNum, led_mode },
		};
		const res: SettingRequest = await gen2Request(device, 'presencec_ui.SetConfig', params);
		const response = onSettingsResponse(res, device);

		if (response) {
			settings['presencec_ui:0'] = { ...uiConfig, led_brightness: brightnessNum, led_mode };
			devices_settings.update_devices_settings(id, settings);
			cacheLedBrightness = led_brightness;
			cacheLedMode = led_mode;
		} else {
			led_brightness = cacheLedBrightness;
			led_mode = cacheLedMode;
		}
		loading = false;
	};
</script>

<Text styleType="label-small" class="mb-xs">
	{$format('presence_ui_led_brightness_label', { default: 'LED brightness' })}
</Text>
<Input
	class="mb-m"
	size="m"
	bind:value={led_brightness}
	type="number"
	min={LED_BRIGHTNESS_MIN}
	max={LED_BRIGHTNESS_MAX}
	error={brightnessError}
	icon="fas fa-sun-bright"
	id="input-led-brightness"
	aria-labelledby="input-led-brightness"
/>

<Text styleType="label-small" class="mb-xs">
	{$format('presence_ui_led_mode_label', { default: 'LED mode' })}
</Text>
<Select
	class="mb-m"
	options={LED_MODE_OPTIONS}
	value={ledModeValue}
	on:change={(e) => (led_mode = e.detail.value)}
	id="select-led-mode"
	aria-label={$format('presence_ui_led_mode_label', { default: 'LED mode' })}
/>

<Button size="m" width="full" disabled={!hasChanges || !!brightnessError} {loading} on:click={save}>
	{$format('button_save', { default: 'Save' })}
</Button>
